import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Mail,
  MailOpen,
  Search,
  Archive,
  ArchiveRestore,
  Trash2,
  Reply,
  Send,
  Loader2,
  Inbox as InboxIcon,
  X,
  PenSquare,
  AlertCircle,
} from 'lucide-react';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';

interface InboxMessage {
  id: string;
  from: string;
  fromName: string | null;
  to: string;
  subject: string;
  text: string;
  html: string;
  snippet: string;
  read: boolean;
  archived: boolean;
  replied?: boolean;
  spamScore: number | null;
  attachmentCount: number;
  receivedAt: Date;
}

function toDate(value: any): Date {
  if (!value) return new Date(0);
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
}

function mapMessage(id: string, data: any): InboxMessage {
  return {
    id,
    from: data.from || '',
    fromName: data.fromName || null,
    to: data.to || '',
    subject: data.subject || '(no subject)',
    text: data.text || '',
    html: data.html || '',
    snippet: data.snippet || (data.text || '').slice(0, 280),
    read: !!data.read,
    archived: !!data.archived,
    replied: !!data.replied,
    spamScore: data.spamScore ?? null,
    attachmentCount: data.attachmentCount || 0,
    receivedAt: toDate(data.receivedAt),
  };
}

function formatDate(d: Date): string {
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

function initialsFor(msg: InboxMessage): string {
  const base = msg.fromName || msg.from || '?';
  return base.trim().charAt(0).toUpperCase() || '?';
}

type FolderFilter = 'inbox' | 'archived';

export function AdminInbox() {
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [folder, setFolder] = useState<FolderFilter>('inbox');
  const [searchTerm, setSearchTerm] = useState('');

  // Compose / reply state
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState<'reply' | 'new'>('new');
  const [toField, setToField] = useState('');
  const [subjectField, setSubjectField] = useState('');
  const [bodyField, setBodyField] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'inbox'), orderBy('receivedAt', 'desc'), limit(200));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setMessages(snap.docs.map((d) => mapMessage(d.id, d.data())));
        setLoading(false);
        setLoadError(null);
      },
      (err) => {
        console.error('[AdminInbox] snapshot error:', err);
        setLoadError(err.message || 'Failed to load inbox');
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return messages.filter((m) => {
      if (folder === 'inbox' && m.archived) return false;
      if (folder === 'archived' && !m.archived) return false;
      if (!term) return true;
      return (
        m.from.toLowerCase().includes(term) ||
        (m.fromName || '').toLowerCase().includes(term) ||
        m.subject.toLowerCase().includes(term) ||
        m.text.toLowerCase().includes(term)
      );
    });
  }, [messages, folder, searchTerm]);

  const selected = useMemo(
    () => messages.find((m) => m.id === selectedId) || null,
    [messages, selectedId],
  );

  const unreadCount = useMemo(
    () => messages.filter((m) => !m.archived && !m.read).length,
    [messages],
  );

  const markRead = useCallback(async (msg: InboxMessage, read: boolean) => {
    try {
      await updateDoc(doc(db, 'inbox', msg.id), { read });
    } catch (err) {
      console.error('[AdminInbox] markRead failed:', err);
    }
  }, []);

  const handleSelect = useCallback(
    (msg: InboxMessage) => {
      setSelectedId(msg.id);
      setComposeOpen(false);
      if (!msg.read) markRead(msg, true);
    },
    [markRead],
  );

  const toggleArchive = useCallback(async (msg: InboxMessage) => {
    try {
      await updateDoc(doc(db, 'inbox', msg.id), { archived: !msg.archived });
    } catch (err) {
      console.error('[AdminInbox] archive failed:', err);
    }
  }, []);

  const handleDelete = useCallback(
    async (msg: InboxMessage) => {
      if (!window.confirm('Delete this message permanently?')) return;
      try {
        await deleteDoc(doc(db, 'inbox', msg.id));
        if (selectedId === msg.id) setSelectedId(null);
      } catch (err) {
        console.error('[AdminInbox] delete failed:', err);
      }
    },
    [selectedId],
  );

  const openReply = useCallback((msg: InboxMessage) => {
    setComposeMode('reply');
    setToField(msg.from);
    setSubjectField(msg.subject.toLowerCase().startsWith('re:') ? msg.subject : `Re: ${msg.subject}`);
    setBodyField(
      `\n\n———\nOn ${formatDate(msg.receivedAt)}, ${msg.fromName || msg.from} wrote:\n> ${msg.text
        .split('\n')
        .join('\n> ')}`,
    );
    setSendError(null);
    setSendSuccess(false);
    setComposeOpen(true);
  }, []);

  const openCompose = useCallback(() => {
    setComposeMode('new');
    setToField('');
    setSubjectField('');
    setBodyField('');
    setSendError(null);
    setSendSuccess(false);
    setComposeOpen(true);
  }, []);

  const handleSend = useCallback(async () => {
    setSendError(null);
    setSendSuccess(false);

    if (!toField.trim() || !toField.includes('@')) {
      setSendError('Enter a valid recipient email.');
      return;
    }
    if (!bodyField.trim()) {
      setSendError('Message body cannot be empty.');
      return;
    }

    setSending(true);
    try {
      const send = httpsCallable(functions, 'sendInboxReply');
      await send({
        to: toField.trim(),
        subject: subjectField.trim() || '(no subject)',
        text: bodyField,
        inReplyTo: composeMode === 'reply' ? selectedId : null,
      });
      setSendSuccess(true);
      setTimeout(() => {
        setComposeOpen(false);
        setSendSuccess(false);
      }, 1200);
    } catch (err: any) {
      console.error('[AdminInbox] send failed:', err);
      setSendError(err?.message || 'Failed to send. Check SendGrid configuration.');
    } finally {
      setSending(false);
    }
  }, [toField, subjectField, bodyField, composeMode, selectedId]);

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-serif text-gray-900 flex items-center gap-2">
            <Mail className="h-6 w-6 text-primary" />
            Inbox
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Mail sent to <span className="font-medium">info@fashdelivery.com</span>
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">
                {unreadCount} unread
              </span>
            )}
          </p>
        </div>
        <Button onClick={openCompose} className="gap-2">
          <PenSquare className="h-4 w-4" />
          Compose
        </Button>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        {/* List panel */}
        <div className="bg-white rounded-xl border border-gray-200 flex flex-col min-h-0">
          {/* Folder tabs + search */}
          <div className="p-3 border-b border-gray-100 space-y-3">
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              {(['inbox', 'archived'] as FolderFilter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFolder(f)}
                  className={cn(
                    'flex-1 text-sm font-medium py-1.5 rounded-md capitalize transition-colors',
                    folder === f ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                  )}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
          </div>

          {/* Message list */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-40 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
              </div>
            ) : loadError ? (
              <div className="flex flex-col items-center justify-center h-40 text-center px-4 text-gray-500">
                <AlertCircle className="h-6 w-6 text-amber-500 mb-2" />
                <p className="text-sm">{loadError}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-center px-4 text-gray-400">
                <InboxIcon className="h-8 w-8 mb-2" />
                <p className="text-sm">
                  {folder === 'inbox' ? 'No messages yet.' : 'Nothing archived.'}
                </p>
              </div>
            ) : (
              filtered.map((msg) => (
                <button
                  key={msg.id}
                  onClick={() => handleSelect(msg)}
                  className={cn(
                    'w-full text-left px-4 py-3 border-b border-gray-50 flex gap-3 transition-colors',
                    selectedId === msg.id ? 'bg-primary/5' : 'hover:bg-gray-50',
                    !msg.read && 'bg-blue-50/40',
                  )}
                >
                  <div
                    className={cn(
                      'w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0',
                      msg.read ? 'bg-gray-200 text-gray-600' : 'bg-primary text-white',
                    )}
                  >
                    {initialsFor(msg)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={cn(
                          'truncate text-sm',
                          msg.read ? 'text-gray-700' : 'font-semibold text-gray-900',
                        )}
                      >
                        {msg.fromName || msg.from}
                      </span>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {formatDate(msg.receivedAt)}
                      </span>
                    </div>
                    <p
                      className={cn(
                        'truncate text-sm',
                        msg.read ? 'text-gray-500' : 'text-gray-800 font-medium',
                      )}
                    >
                      {msg.subject}
                    </p>
                    <p className="truncate text-xs text-gray-400">{msg.snippet}</p>
                    {msg.replied && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-green-600 mt-0.5">
                        <Reply className="h-3 w-3" /> Replied
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Reading / compose panel */}
        <div className="bg-white rounded-xl border border-gray-200 flex flex-col min-h-0">
          {composeOpen ? (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                <h2 className="font-medium text-gray-900">
                  {composeMode === 'reply' ? 'Reply' : 'New message'}
                </h2>
                <button
                  onClick={() => setComposeOpen(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="p-5 space-y-3 flex-1 flex flex-col min-h-0">
                <div>
                  <label className="text-xs font-medium text-gray-500">To</label>
                  <Input
                    value={toField}
                    onChange={(e) => setToField(e.target.value)}
                    placeholder="recipient@example.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Subject</label>
                  <Input
                    value={subjectField}
                    onChange={(e) => setSubjectField(e.target.value)}
                    placeholder="Subject"
                    className="mt-1"
                  />
                </div>
                <div className="flex-1 flex flex-col min-h-0">
                  <label className="text-xs font-medium text-gray-500">Message</label>
                  <textarea
                    value={bodyField}
                    onChange={(e) => setBodyField(e.target.value)}
                    placeholder="Write your message…"
                    className="mt-1 flex-1 min-h-[160px] w-full rounded-lg border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  />
                </div>
                {sendError && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {sendError}
                  </div>
                )}
                {sendSuccess && (
                  <div className="text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">
                    Sent successfully.
                  </div>
                )}
              </div>
              <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setComposeOpen(false)} disabled={sending}>
                  Cancel
                </Button>
                <Button onClick={handleSend} disabled={sending} className="gap-2">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send
                </Button>
              </div>
            </div>
          ) : selected ? (
            <div className="flex flex-col h-full">
              {/* Message header */}
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="text-lg font-medium text-gray-900">{selected.subject}</h2>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      title={selected.read ? 'Mark unread' : 'Mark read'}
                      onClick={() => markRead(selected, !selected.read)}
                      className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
                    >
                      {selected.read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                    </button>
                    <button
                      title={selected.archived ? 'Unarchive' : 'Archive'}
                      onClick={() => toggleArchive(selected)}
                      className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
                    >
                      {selected.archived ? (
                        <ArchiveRestore className="h-4 w-4" />
                      ) : (
                        <Archive className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      title="Delete"
                      onClick={() => handleDelete(selected)}
                      className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                    {initialsFor(selected)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {selected.fromName || selected.from}
                      {selected.fromName && (
                        <span className="text-gray-400 font-normal ml-1">&lt;{selected.from}&gt;</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-400">
                      to {selected.to} · {selected.receivedAt.toLocaleString()}
                    </p>
                  </div>
                </div>
                {selected.spamScore != null && selected.spamScore >= 5 && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-1.5">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Possible spam (score {selected.spamScore})
                  </div>
                )}
              </div>

              {/* Message body */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {selected.html ? (
                  <div
                    className="prose prose-sm max-w-none text-gray-800"
                    // Inbound HTML is untrusted; rendered in admin-only view.
                    dangerouslySetInnerHTML={{ __html: selected.html }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800">
                    {selected.text || '(no content)'}
                  </pre>
                )}
              </div>

              {/* Actions */}
              <div className="px-5 py-3 border-t border-gray-100">
                <Button onClick={() => openReply(selected)} className="gap-2">
                  <Reply className="h-4 w-4" />
                  Reply
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Mail className="h-10 w-10 mb-3" />
              <p className="text-sm">Select a message to read</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
